/** Settings → Team: the back office creates accesses (position + secret link by e-mail) and manages
 * them. D-120: the positions (owner, manager, advisor, front desk, accountant, Academy manager,
 * trainer, marketing, support) and their rights are versioned data read from the API, shown as a
 * readable matrix; a trainer has teaching languages (French, English, German). */

import {
  type MembershipRoleEnum,
  type TeamPosition,
  useTeamInvitationsCreate,
  useTeamInvitationsList,
  useTeamInvitationsResendCreate,
  useTeamInvitationsRevokeCreate,
  useTeamMembersList,
  useTeamMembersPartialUpdate,
  useTeamPositionsRetrieve,
} from "@agencyos/api-client";
import { Avatar, Badge, Button, Checkbox, GlassCard, SelectField, Skeleton, SoftToggle, TextField } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import { SettingsLayout } from "../components/SettingsLayout";
import { fieldErrors, useHasPermission, usePosition } from "../lib/settings";

const HIGH: string[] = ["owner", "manager"];
type Language = "fr" | "en" | "de";

function useRefreshTeam() {
  const queryClient = useQueryClient();
  return () =>
    void queryClient.invalidateQueries({
      predicate: (query) => String(query.queryKey[0] ?? "").startsWith("/api/v1/team/"),
    });
}

/** The positions of the matrix (API), with what the current person may grant. */
function usePositions(): { positions: TeamPosition[]; teaching: Language[]; ready: boolean } {
  const matrix = useTeamPositionsRetrieve();
  const data = matrix.data?.status === 200 ? matrix.data.data : undefined;
  return {
    positions: data ? [...data.positions] : [],
    teaching: (data?.teaching_languages ?? ["fr", "en", "de"]) as Language[],
    ready: data !== undefined,
  };
}

function LanguagesPicker({ value, onChange, teaching, disabled = false }: { value: Language[]; onChange: (next: Language[]) => void; teaching: Language[]; disabled?: boolean }) {
  const { t } = useTranslation();
  return (
    <fieldset className="row wrap team-languages">
      <legend>{t("access.team.languages")}</legend>
      {teaching.map((code) => (
        <Checkbox
          key={code}
          label={t(`access.team.language.${code}`)}
          checked={value.includes(code)}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked ? [...value, code] : value.filter((c) => c !== code))}
        />
      ))}
    </fieldset>
  );
}

function Invite() {
  const { t } = useTranslation();
  const refresh = useRefreshTeam();
  const { positions, teaching } = usePositions();
  const grantable = positions.filter((p) => p.grantable);
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState<MembershipRoleEnum>("advisor");
  const [languages, setLanguages] = useState<Language[]>([]);
  const invite = useTeamInvitationsCreate({ mutation: { onSuccess: refresh } });
  const errors = fieldErrors(invite.error);
  const chosen = positions.find((p) => p.code === role);

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    invite.mutate(
      { data: { email, full_name: fullName, role, ...(chosen?.languages ? { languages } : {}) } },
      {
        onSuccess: () => {
          setEmail("");
          setFullName("");
          setLanguages([]);
        },
      },
    );
  };

  return (
    <GlassCard as="section" className="stack">
      <h2>{t("team.invite")}</h2>
      <p className="muted">{t("team.inviteIntro")}</p>
      <form className="stack" onSubmit={onSubmit} noValidate>
        <TextField label={t("team.fullName")} value={fullName} onChange={(e) => setFullName(e.target.value)} />
        <TextField label={t("team.email")} type="email" value={email} error={errors.email} onChange={(e) => setEmail(e.target.value)} />
        <SelectField
          label={t("access.team.position")}
          value={role}
          options={grantable.map((p) => ({ value: p.code, label: p.label }))}
          onChange={(e) => setRole(e.target.value as MembershipRoleEnum)}
        />
        {chosen ? <p className="muted">{chosen.description}</p> : null}
        {chosen?.languages ? <LanguagesPicker value={languages} onChange={setLanguages} teaching={teaching} /> : null}
        {chosen?.languages ? <p className="muted">{t("access.team.languagesHint")}</p> : null}
        {invite.isError && !errors.email ? (
          <p role="alert" className="ko">
            {errors.detail ?? errors.role ?? errors.languages ?? t("common.error")}
          </p>
        ) : null}
        <Button type="submit" disabled={invite.isPending || !email.trim()}>
          {t("team.send")}
        </Button>
        {invite.isSuccess ? (
          <p role="status" className="ok">
            {t("team.sent")}
          </p>
        ) : null}
      </form>
    </GlassCard>
  );
}

function Members() {
  const { t } = useTranslation();
  const refresh = useRefreshTeam();
  const { positions, teaching } = usePositions();
  const currentPosition = usePosition();
  const members = useTeamMembersList();
  const update = useTeamMembersPartialUpdate({ mutation: { onSuccess: refresh } });
  const list = members.data?.status === 200 ? members.data.data : [];
  const error = fieldErrors(update.error);
  const label = (code: string) => positions.find((p) => p.code === code)?.label ?? t(`team.roles.${code}`);
  return (
    <GlassCard as="section" className="stack">
      <h2>{t("team.members")}</h2>
      {members.isPending ? <Skeleton /> : null}
      {update.isError ? (
        <p role="alert" className="ko">
          {error.detail ?? error.role ?? error.languages ?? t("common.error")}
        </p>
      ) : null}
      <ul className="plain-list">
        {list.map((member) => {
          const role = member.role ?? "advisor";
          const locked = currentPosition !== "owner" && HIGH.includes(role);
          const options = (locked ? positions : positions.filter((p) => p.grantable || p.code === role)).map((p) => ({ value: p.code, label: p.label }));
          const hasLanguages = positions.find((p) => p.code === role)?.languages;
          const memberLanguages = (Array.isArray(member.languages) ? member.languages : []) as Language[];
          return (
            <li key={member.id} className="member-row">
              <Avatar name={member.full_name || member.email} size="lg" />
              <span className="member-row__who">
                <strong>{member.full_name || member.email}</strong>
                <span className="muted">{member.email}</span>
                {member.is_active ? null : <Badge tone="danger">{t("team.inactive")}</Badge>}
              </span>
              <div className="member-row__controls">
                <SelectField
                  label={t("access.team.position")}
                  value={role}
                  disabled={locked}
                  options={options.length ? options : [{ value: role, label: label(role) }]}
                  onChange={(e) => update.mutate({ id: member.id, data: { role: e.target.value as MembershipRoleEnum } })}
                />
                {hasLanguages ? (
                  <LanguagesPicker
                    value={memberLanguages}
                    teaching={teaching}
                    disabled={locked}
                    onChange={(next) => update.mutate({ id: member.id, data: { languages: next } })}
                  />
                ) : null}
                <SoftToggle
                  label={t("team.access")}
                  checked={Boolean(member.is_active)}
                  disabled={locked}
                  onChange={(value) => update.mutate({ id: member.id, data: { is_active: value } })}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </GlassCard>
  );
}

function Invitations() {
  const { t, i18n } = useTranslation();
  const refresh = useRefreshTeam();
  const { positions } = usePositions();
  const invitations = useTeamInvitationsList();
  const resend = useTeamInvitationsResendCreate({ mutation: { onSuccess: refresh } });
  const revoke = useTeamInvitationsRevokeCreate({ mutation: { onSuccess: refresh } });
  const list = invitations.data?.status === 200 ? invitations.data.data.results : [];
  const locale = i18n.language === "en" ? "en-GB" : i18n.language;
  if (!list.length) return null;
  return (
    <GlassCard as="section" className="stack">
      <h2>{t("team.invitations")}</h2>
      <ul className="plain-list">
        {list.map((invitation) => (
          <li key={invitation.id} className="member-row member-row--invite">
            <Avatar name={invitation.full_name || invitation.email} size="lg" />
            <span className="member-row__who">
              <strong>{invitation.full_name || invitation.email}</strong>
              <span className="muted">{positions.find((p) => p.code === invitation.role)?.label ?? t(`team.roles.${invitation.role}`)}</span>
            </span>
            <span className="muted member-row__meta">
              {t("team.expires", {
                date: new Date(invitation.expires_at).toLocaleString(locale, { timeZone: "Africa/Douala" }),
              })}
            </span>
            <Badge tone={invitation.status === "accepted" ? "success" : invitation.status === "pending" ? "default" : "danger"}>
              {t(`team.status.${invitation.status}`)}
            </Badge>
            {invitation.status === "pending" || invitation.status === "expired" ? (
              <span className="row">
                <Button variant="secondary" onClick={() => resend.mutate({ id: invitation.id })}>
                  {t("team.resend")}
                </Button>
                {invitation.status === "pending" ? (
                  <Button variant="ghost" onClick={() => revoke.mutate({ id: invitation.id })}>
                    {t("team.revoke")}
                  </Button>
                ) : null}
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    </GlassCard>
  );
}

const LEVEL_TONE: Record<string, "success" | "default" | "offer" | "warning"> = {
  full: "success",
  write: "offer",
  own: "warning",
  read: "default",
};

/** The readable matrix: a row per area, a column per position (D-120). */
function RightsMatrix() {
  const { t } = useTranslation();
  const matrix = useTeamPositionsRetrieve();
  const data = matrix.data?.status === 200 ? matrix.data.data : undefined;
  if (!data) return <Skeleton style={{ height: 160 }} />;
  return (
    <GlassCard as="section" className="stack" aria-labelledby="rights-matrix">
      <h2 id="rights-matrix">{t("access.team.matrix")}</h2>
      <p className="muted">{t("access.team.matrixIntro", { version: data.version })}</p>
      <div className="table-wrap">
        <table className="table team-matrix">
          <thead>
            <tr>
              <th scope="col">{t("access.team.area")}</th>
              {data.positions.map((p) => (
                <th key={p.code} scope="col">
                  {p.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rights.map((row) => (
              <tr key={row.key}>
                <th scope="row">{row.label}</th>
                {data.positions.map((p) => {
                  const level = row.levels[p.code] ?? "none";
                  return (
                    <td key={p.code}>
                      {level === "none" ? <span className="muted" aria-label={t("access.level.none")}>—</span> : <Badge tone={LEVEL_TONE[level] ?? "default"}>{t(`access.level.${level}`)}</Badge>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="muted">{t("access.team.matrixLegend")}</p>
    </GlassCard>
  );
}

export function TeamPage() {
  const { t } = useTranslation();
  const allowed = useHasPermission("team.manage");
  return (
    <SettingsLayout title={t("team.title")} intro={t("team.intro")}>
      {allowed ? (
        <div className="stack">
          <div className="bento">
            <div className="stack bento__wide">
              <Members />
              <Invitations />
            </div>
            <div className="bento__narrow">
              <Invite />
            </div>
          </div>
          <RightsMatrix />
        </div>
      ) : (
        <div className="stack">
          <GlassCard role="note">{t("team.onlyAdmins")}</GlassCard>
          <RightsMatrix />
        </div>
      )}
    </SettingsLayout>
  );
}
